import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import React, { useState } from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { enUS } from "date-fns/locale";
import { LocalizationProvider } from "@mui/x-date-pickers";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import styled from "styled-components";
import { Box, createTheme, ThemeProvider } from "@mui/material";
import { format } from "date-fns";
import { isValidDate } from "../../../../providers/helperFunctions";

type Props = {
  label: string;
  value: string | undefined;
  handleChange: (newValue: Date) => void;
  minValue?: Date;
  maxValue?: Date;
};

interface StyledDatePickerWrapperProps {
  theme: any;
}

const StyledDatePickerWrapper = styled(Box)<StyledDatePickerWrapperProps>(
  ({ theme }) => ({
    position: "relative",
    "& .MuiFormControl-root": {
      width: "140px",
      marginTop: "4px",
    },
    ".MuiOutlinedInput-root": {
      height: "40px",
      paddingTop: "8px",
      display: "flex",
      alignItems: "center",
      fontFamily: "var(--font-Medium)",
      fontSize: "12px",
    },

    ".MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent",
    },

    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent",
    },

    ".Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent",
    },

    ".Mui-error .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent",
    },

    ".MuiOutlinedInput-input": {
      padding: "0",
    },

    ".MuiFormLabel-root": {
      fontFamily: "var(--font-Medium)",
      fontSize: "10px",
      color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
      transform: "translate(0, -4px) scale(1)",
    },

    ".Mui-focused.MuiFormLabel-root": {
      color: theme.colors_ds.PRIMARY_ICON_MAIN,
    },

    ".MuiFormHelperText-root": {
      fontFamily: "var(--font-Medium)",
      fontSize: "10px",
      color: theme.colors_ds.DANGER_GRAPH_DEFICIT,
      position: "absolute",
      bottom: "-16px",
      left: "-8px",
    },

    legend: {
      display: "none",
    },

    fieldset: {
      top: "0",
    },

    ".MuiButtonBase-root": {
      backgroundColor: "transparent",
      marginTop: "-12px",
    },
  })
);

const CommonDatePicker = ({
  label,
  value,
  handleChange,
  minValue,
  maxValue,
}: Props) => {
  const theme = useTheme();

  const baseTheme = createTheme();

  const newTheme = createTheme({
    ...baseTheme,
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: "8px",
            boxShadow: "var(--box-shadow-general)",
            marginTop: "8px",
            marginLeft: "-14px",
            border: `1px solid ${theme.colors_ds.PRIMARY_ICON_MAIN}`,
          },
        },
      },
      // @ts-ignore
      MuiPickersCalendarHeader: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
          },
          label: {
            fontFamily: "var(--font-Bold)",
          },
        },
      },
      MuiPickersArrowSwitcher: {
        styleOverrides: {
          spacer: {
            width: "2px",
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            fontFamily: "var(--font-Medium)",
            fontSize: "12px",
            "&[aria-colindex='6']": {
              color: theme.colors_ds.PRIMARY_TEXT_MAIN,
            },
            "&[aria-colindex='7']": {
              color: theme.colors_ds.PRIMARY_TEXT_MAIN,
            },
            "&[aria-selected='true']": {
              color: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
              fontFamily: "var(--font-Bold)",
            },
          },
          today: {
            borderColor: "transparent",
          },
        },
      },
      MuiDayCalendar: {
        styleOverrides: {
          weekDayLabel: {
            fontFamily: "var(--font-Bold)",
            "&[aria-label='Saturday']": {
              color: theme.colors_ds.PRIMARY_TEXT_MAIN,
            },
            "&[aria-label='Sunday']": {
              color: theme.colors_ds.PRIMARY_TEXT_MAIN,
            },
          },
          weekContainer: {
            fontFamily: "var(--font-Bold)",
          },
        },
      },
      MuiPickersYear: {
        styleOverrides: {
          yearButton: {
            fontFamily: "var(--font-Medium)",
            fontSize: "12px",
            "&.Mui-selected": {
              fontFamily: "var(--font-Bold)",
              fontSize: "12px",
              borderRadius: "8px",
            },
            "&.Mui-disabled": {
              opacity: 0.6,
            },
            "&:hover": {
              borderRadius: "8px",
            },
          },
        },
      },
      MuiPickersMonth: {
        styleOverrides: {
          monthButton: {
            fontFamily: "var(--font-Medium)",
            fontSize: "12px",
            "&.Mui-selected": {
              fontFamily: "var(--font-Bold)",
              fontSize: "12px",
              borderRadius: "8px",
            },
            "&.Mui-disabled": {
              opacity: 0.6,
            },
            "&:hover": {
              borderRadius: "8px",
            },
          },
        },
      },
    },
  });

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={{ ...enUS, options: { weekStartsOn: 1 } }}
      // MUI's default letter-month placeholder is the full "MMMM"; override it
      // to "MMM" so the empty-field mask matches our "MMM dd yyyy" format
      // (renders "MMM DD YYYY" instead of a clipped "MMMM DD YY").
      localeText={{
        fieldMonthPlaceholder: (params: any) =>
          params.contentType === "letter" ? "MMM" : "MM",
      }}
    >
      <ThemeProvider theme={newTheme}>
        <StyledDatePickerWrapper theme={theme}>
          <DatePicker
            views={["year", "month", "day"]}
            value={value ? new Date(value) : null}
            disableHighlightToday={false}
            disablePast={false}
            label={label}
            yearsOrder="desc"
            maxDate={maxValue}
            minDate={minValue}
            onChange={(newValue) => {
              if (newValue && isValidDate(newValue)) {
                handleChange(newValue);
              }
            }}
            openTo={"day"}
            slots={{
              previousIconButton: KeyboardArrowLeftRoundedIcon,
              nextIconButton: KeyboardArrowRightRoundedIcon,
              switchViewIcon: KeyboardArrowDownRoundedIcon,
            }}
            slotProps={{
              textField: {
                inputProps: {
                  readOnly: true,
                },
              },
              switchViewIcon: {
                sx: {
                  width: "24px",
                  height: "24px",
                  fontSize: "2rem",
                  padding: 0,
                  cursor: "pointer",
                  borderRadius: "50%",
                  color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
                },
              },
              previousIconButton: {
                sx: {
                  width: "32px",
                  height: "32px",
                  cursor: "pointer",
                  padding: "4px",
                  borderRadius: "50%",
                  color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
                  "&:hover": {
                    backgroundColor: theme.colorTokens.N[30],
                  },
                },
              },
              nextIconButton: {
                sx: {
                  width: "32px",
                  height: "32px",
                  cursor: "pointer",
                  padding: "4px",
                  borderRadius: "50%",
                  color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
                  "&:hover": {
                    backgroundColor: theme.colorTokens.N[30],
                  },
                },
              },
            }}
            format="MMM dd yyyy" // Use format here
          />
        </StyledDatePickerWrapper>
      </ThemeProvider>
    </LocalizationProvider>
  );
};

export default CommonDatePicker;
